import React from "react";
import styled from "styled-components";
import { Container } from "@mui/material";
import SEO from "../components/SEO";
import { settings } from "../data/settings";
import GuideLeadForm from "../components/GuideLeadForm";
import SeoInternalLinks from "../components/SeoInternalLinks";

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: var(--my-color3);
`;

const SubTitle = styled.h2`
  font-size: 1.4rem;
  margin-top: 2rem;
  margin-bottom: 0.7rem;
  color: var(--my-color3);
`;

const Paragraph = styled.p`
  line-height: 1.7;
  margin-bottom: 1rem;
`;

const List = styled.ul`
  margin: 0 0 1rem 1.2rem;
  padding: 0;
  line-height: 1.7;
`;

const CTA = styled.a`
  display: inline-block;
  margin-top: 0.6rem;
  margin-right: 0.75rem;
  text-decoration: none;
  background: var(--my-color3);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 700;
  text-transform: uppercase;
`;

const faqLd = [
  {
    "@type": "Question",
    name: "Quand choisir un freelance ?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Le freelance convient à un projet bien cadré, un budget optimisé et une relation directe avec la personne qui conçoit et livre.",
    },
  },
  {
    "@type": "Question",
    name: "Quand choisir une agence ?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Une agence peut être pertinente pour des besoins multi-expertises en parallèle et des organisations avec plusieurs parties prenantes.",
    },
  },
  {
    "@type": "Question",
    name: "Un freelance peut-il gérer un projet complet ?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Oui, avec un cadrage solide, une feuille de route et des points de suivi réguliers.",
    },
  },
  {
    "@type": "Question",
    name: "Et si le besoin grandit ?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Le projet peut évoluer vers un mode hybride avec des experts ponctuels si nécessaire.",
    },
  },
  {
    "@type": "Question",
    name: "Comment choisir concrètement ?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Comparez plusieurs propositions sur la méthode, les livrables et les indicateurs de succès, pas seulement sur le prix.",
    },
  },
];

export default function FreelanceOuAgencePage() {
  return (
    <Container
      sx={{ py: 4 }}
      style={{ maxWidth: "900px", marginTop: settings.navbar.height }}
    >
      <SEO
        title="Freelance ou agence pour créer son site web ? | DigiBuddy"
        description="Freelance ou agence : budget, délais, flexibilité et critères pour choisir le bon partenaire selon la taille et la maturité de votre projet."
        keywords="freelance ou agence pour créer son site, développeur freelance ou agence web, choisir prestataire création site internet, coût agence web vs freelance"
        url="https://www.digibuddy.fr/freelance-ou-agence-pour-creer-son-site"
        canonical="https://www.digibuddy.fr/freelance-ou-agence-pour-creer-son-site"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Accueil",
                item: "https://www.digibuddy.fr/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Freelance ou agence pour créer son site",
                item: "https://www.digibuddy.fr/freelance-ou-agence-pour-creer-son-site",
              },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqLd,
          },
        ]}
      />

      <Title>Freelance ou agence : qui choisir pour créer son site&nbsp;?</Title>
      <Paragraph>
        Le bon choix dépend de votre urgence, de votre budget et du cadre du
        projet. L’important est d’aligner le mode de collaboration avec vos
        objectifs : pas de réponse unique, mais des critères objectifs pour
        trancher.
      </Paragraph>

      <SubTitle>Freelance : atouts</SubTitle>
      <List>
        <li>Relation directe, arbitrages plus rapides</li>
        <li>Budget souvent plus accessible pour un périmètre équivalent</li>
        <li>Grande flexibilité sur les priorités et les itérations</li>
      </List>

      <SubTitle>Agence : atouts</SubTitle>
      <List>
        <li>Plusieurs expertises mobilisables en parallèle</li>
        <li>Capacité à absorber de gros volumes ou plusieurs projets</li>
        <li>Process formalisés pour les organisations complexes</li>
      </List>

      <SubTitle>Cinq critères pour trancher</SubTitle>
      <List>
        <li>Complexité technique et intégrations</li>
        <li>Niveau d’autonomie souhaité côté client</li>
        <li>Budget total et rythme d’investissement</li>
        <li>Délai de mise en ligne visé</li>
        <li>Besoin d’accompagnement long terme (maintenance, évolutions)</li>
      </List>

      <SubTitle>Recommandation pragmatique</SubTitle>
      <Paragraph>
        Pour une PME ou un indépendant qui veut une mise en ligne rapide et un
        pilotage simple, un freelance expérimenté avec une méthode claire est
        souvent le meilleur compromis impact / prix / délai — surtout sur un
        site vitrine ou une refonte ciblée.
      </Paragraph>

      <SubTitle>FAQ</SubTitle>
      <Paragraph>
        <strong>Quand choisir un freelance&nbsp;?</strong>
        <br />
        Projet cadré, budget maîtrisé, besoin d’interlocuteur unique du brief à
        la livraison.
      </Paragraph>
      <Paragraph>
        <strong>Quand choisir une agence&nbsp;?</strong>
        <br />
        Besoins multiples simultanés (stratégie, contenu, ads, dev lourd) et
        organisation avec plusieurs métiers impliqués.
      </Paragraph>
      <Paragraph>
        <strong>Un freelance peut-il gérer un projet complet&nbsp;?</strong>
        <br />
        Oui, avec cadrage, roadmap et points réguliers — comme en agence, mais
        avec moins de couches intermédiaires.
      </Paragraph>
      <Paragraph>
        <strong>Et si le besoin grandit&nbsp;?</strong>
        <br />
        On peut faire évoluer vers un mode hybride (experts ponctuels) sans
        remettre en cause la base livrée.
      </Paragraph>
      <Paragraph>
        <strong>Comment choisir concrètement&nbsp;?</strong>
        <br />
        Comparez 2 ou 3 propositions sur la méthode, les livrables et les KPI,
        pas uniquement sur le tarif journalier ou forfaitaire.
      </Paragraph>

      <CTA href="/#contact">Parler de mon projet</CTA>
      <CTA href="/combien-coute-site-web-sur-mesure" style={{ background: "var(--my-color2)" }}>
        Prix d’un site sur mesure
      </CTA>

      <SeoInternalLinks
        links={[
          { to: "/combien-coute-site-web-sur-mesure", label: "Combien coûte un site web sur mesure ?" },
          { to: "/creation-site-web-sur-mesure", label: "Création de site web sur mesure" },
          { to: "/", label: "Accueil DigiBuddy" },
        ]}
      />

      <GuideLeadForm source="article-freelance-ou-agence" />
    </Container>
  );
}
